// Types for reservation system based on backend API

// Backend enums
export const BookingStatus = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected', 
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  INCOMPLETED: 'Inomplete', // Note: Backend has typo "Inomplete"
  CANCELED: 'Canceled'
} as const;

export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];

// WebSocket message types
export interface BookingAction {
  bookingId: number;
  action: 'ACCEPT' | 'REJECT' | 'COMPLETE' | 'CANCEL';
  tableId?: number;
  message?: string;
}

export interface BookingResponse {
  id: number;
  bookedId: string;
  username: string;
  email: string;
  phoneNo: string;
  message?: string;
  tableNo: number;
  time: string;
  date: string;
  status: BookingStatus;
  guestNo: number;
  restaurantTable?: RestaurantTable;
  createdAt?: string;
  updatedAt?: string;
}

export const TableStatus = {
  AVAILABLE: 'Available',
  OCCUPIED: 'Occupied',
  RESERVED: 'Reserved',
  MAINTENANCE: 'Maintenance'
} as const;

export type TableStatus = typeof TableStatus[keyof typeof TableStatus];

// Backend entities
// Restaurant table reference for merged tables
export interface RestaurantTableReference {
  id: number;
  name: string;
}

export interface ReserveTable {
  id: number;
  bookedId: string;
  username: string;
  email: string;
  phoneNo: string;
  message?: string;
  tableNo?: number; // Optional - legacy field
  time: string;
  date: string; // LocalDate from backend (YYYY-MM-DD)
  status: BookingStatus;
  guestNo: number;
  restaurantTable?: RestaurantTable; // Legacy single table
  restaurantTableId?: RestaurantTableReference[]; // Array of merged tables {id, name} - Optional as backend may not always include it
  mergeGroupName?: string; // Merge group name for merged table reservations
  mergeGroupColor?: string; // Merge group color for merged table reservations
}

export interface RestaurantTable {
  id: number;
  name: string;
  seats: number;
  status: TableStatus;
  location: string;
  description: string;
  shape: string;
  color: string;
  xposition?: number;
  yposition?: number;
  floor?: string; // Legacy field - kept for backward compatibility
  floorId: number; // Primary floor identifier used by backend
  width?: number;
  height?: number;
  rotation?: number;
  createdAt?: string;
  updatedAt?: string;
}

// Backend DTOs
export interface BookingRequestDto {
  username: string;
  email: string;
  phoneNo: string; // Must be exactly 10 digits
  message?: string;
  tableNo: number; // Legacy single table
  restaurantTableId: number[]; // Array of table IDs for merge support
  date: string; // LocalDate format: YYYY-MM-DD
  time: string;
  guestNo: number;
  // Optional merge group metadata (backend-driven, no localStorage)
  mergeGroupName?: string;
  mergeGroupColor?: string;
}

export interface BookingResponseDto {
  username: string;
  email: string;
  phoneNo: string;
  tableNo: number;
  time: string;
  date: string;
}

export interface RejectRequestDto {
  bookingId: number;
  message?: string;
  bookingStatus: BookingStatus;
  restaurantTableId: number[]; // Backend expects List<Long> (array of numbers)
  // Optional merge group metadata (for large party bookings with 2+ tables)
  mergeGroupName?: string;
  mergeGroupColor?: string;
}

export interface AcceptRessponseDto {
  message: string;
}

export interface ReserveTableDTO {
  id?: number;
  bookedId: string;
  username: string;
  email: string;
  phoneNo: string;
  message?: string;
  time: string;
  date: string;
  status: BookingStatus;
  guestNo: number;
  restaurantTableId: Array<{ id: number; name: string }>;
}

export interface RestaurantTableRequest {
  id?: number; // Optional for create, required for update
  name: string;
  seats: number;
  status: TableStatus;
  location: string;
  description: string;
  shape: string;
  color: string;
  xposition: number;
  yposition: number;
  floorId: number; // Backend expects floorId (number), not floor (string)
  width: number;
  height: number;
  rotation: number;
}

export interface PaginatedResponse<T> {
  content: T[];
  pageable: {
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    pageSize: number;
    pageNumber: number;
    unpaged: boolean;
    paged: boolean;
  };
  last: boolean;
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

// Converted types for UI compatibility
export interface Reservation {
  id: string;
  bookedId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  date: string;
  time: string;
  guestNo: number;
  status: 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled' | 'incompleted';
  tableId?: string;
  tableNo?: number;
  specialRequests?: string;
  createdAt: string;
  restaurantTable?: RestaurantTable;
  restaurantTableId?: RestaurantTableReference[];
  mergeGroupName?: string; // Merge group name for merged table reservations
  mergeGroupColor?: string; // Merge group color for merged table reservations
}

// Utility functions to convert between backend and frontend formats
export const convertReserveTableToReservation = (reserveTable: ReserveTable): Reservation => {
  const statusMap: Record<BookingStatus, Reservation['status']> = {
    [BookingStatus.PENDING]: 'pending',
    [BookingStatus.ACCEPTED]: 'accepted',
    [BookingStatus.REJECTED]: 'rejected',
    [BookingStatus.COMPLETED]: 'completed',
    [BookingStatus.INCOMPLETED]: 'incompleted',
    [BookingStatus.CANCELED]: 'cancelled'
  };

  return {
    id: reserveTable.id.toString(),
    bookedId: reserveTable.bookedId,
    customerName: reserveTable.username,
    customerEmail: reserveTable.email,
    customerPhone: reserveTable.phoneNo,
    date: reserveTable.date,
    time: reserveTable.time,
    guestNo: reserveTable.guestNo,
    specialRequests: reserveTable.message,
    status: statusMap[reserveTable.status] || 'pending',
    createdAt: new Date().toISOString(), // Backend doesn't provide this, use current time
    tableId: reserveTable.tableNo?.toString() || '0', // Handle undefined tableNo
    tableNo: reserveTable.tableNo || 0, // Handle undefined tableNo
    restaurantTableId: reserveTable.restaurantTableId || [], // Include new array structure
    mergeGroupName: reserveTable.mergeGroupName, // Include merge group name
    mergeGroupColor: reserveTable.mergeGroupColor // Include merge group color
  };
};

export const convertReservationStatusToBookingStatus = (status: string): BookingStatus => {
  switch (status.toLowerCase()) {
    case 'accepted':
      return BookingStatus.ACCEPTED;
    case 'cancelled':
      return BookingStatus.REJECTED;
    case 'completed':
      return BookingStatus.COMPLETED;
    case 'incompleted':
      return BookingStatus.INCOMPLETED;
    case 'canceled':
      return BookingStatus.CANCELED;
    default:
      return BookingStatus.PENDING;
  }
};
